import { findTranslatedTextForUser } from "../chatHelper/ChatHelper";

export class TranslatedMessagesSpeaker {
  constructor() {
    this.queue = [];
    this.audio = null;
    this.playing = false;
    this.spokenMessages = new Set();
    this.currentMessages = [];

    this.pendingProcess = false;
    this.processing = false;

    this.processingTimer = null;
    this.queueTimeout = 5000; // ms
    this.stopped = true;
    this.volume = 1.0;
  }

  setVolume(value) {
    this.volume = Math.max(0, Math.min(1, value));
    if (this.audio) {
      this.audio.volume = this.volume;
    }
  }

  cleanProcessed() {
    if (this.spokenMessages.size > 1000) {
      const last = Array.from(this.spokenMessages).slice(-500);
      this.spokenMessages = new Set(last);
    }
  }

  stop() {
    this.stopped = true;
    // Stop any playing audio
    if (this.audio) {
      window.dispatchEvent(new CustomEvent("emotii-playback-end", { detail: { userId: this.currentUserId } }));
      this.audio.pause();
      this.audio.src = "";
      this.audio = null;
    }

    // Cancel any pending timers
    this.cancelScheduledProcessing();

    // Reset flags
    this.playing = false;
    this.processing = false;
    this.pendingProcess = false;

    const messagesIDS = this.currentMessages.map((msg) => msg.id);
    messagesIDS.forEach((id) => this.spokenMessages.add(id));
  }

  markAllAsSpoken(messages) {
    if (!messages || messages.length === 0) return;
    messages.forEach((msg) => {
      if (msg?.id) {
        this.spokenMessages.add(msg.id);
      }
    });
  }

  getSpeakableMessages(messages, user, maxWait = 5000) {
    const now = Date.now();
    const result = [];

    const sorted = [...messages].sort(
      (a, b) => new Date(a.device_sent_date) - new Date(b.device_sent_date)
    );

    const lastIndex = sorted.length - 1;

    for (let i = 0; i < sorted.length; i++) {
      const msg = sorted[i];
      if (this.spokenMessages.has(msg.id)) continue;

      const sentAt = new Date(msg.device_sent_date).getTime();
      const age = now - sentAt;

      const translated = findTranslatedTextForUser(
        msg.call_message_translations,
        user
      );

      if (translated) {
        console.log("[TranslatedMessagesSpeaker][TTSSync] message became speakable", {
          id: msg.id,
          deviceSentDate: msg.device_sent_date,
          translationReadyAfterMs: age,
        });
        result.push({ ...msg, translation: translated.translation });
      } else if (age > maxWait && i !== lastIndex) {
        this.spokenMessages.add(msg.id); // mark old untranslated as processed
      } else {
        break; // wait for translation
      }
    }

    return result;
  }

  async convertToAudio(speakAudio, language, text, speakerUserId, messageMeta) {
    const startedAt = Date.now();
    return new Promise((resolve) => {
      speakAudio(
        text,
        language,
        (response) => {
          const url = URL.createObjectURL(response.data);
          console.log("[TranslatedMessagesSpeaker][TTSSync] TTS audio ready", {
            id: messageMeta?.id,
            ttsConversionMs: Date.now() - startedAt,
          });
          resolve(url);
        },
        (error) => {
          console.error("Audio conversion failed:", error);
          resolve(null);
        },
        speakerUserId
      );
    });
  }

  async playAudio(url, outputDeviceId, setDimVolume, userId, messageMeta) {
    return new Promise((resolve) => {
      if (this.stopped) {
        URL.revokeObjectURL(url);
        resolve(false);
        return;
      }

      this.currentUserId = userId;
      const audio = new Audio(url);
      audio.volume = this.volume;
      this.audio = audio;

      if (outputDeviceId && typeof audio.setSinkId === "function") {
        audio.setSinkId(outputDeviceId).catch(console.warn);
      }

      const finish = (success) => {
        window.dispatchEvent(new CustomEvent("emotii-playback-end", { detail: { userId } }));
        URL.revokeObjectURL(url);
        this.audio = null;
        resolve(success);
      };

      audio.onended = () => finish(true);
      audio.onerror = () => finish(false);

      audio.play()
        .then(() => {
          if (messageMeta?.deviceSentDate) {
            console.log("[TranslatedMessagesSpeaker][TTSSync] TTS playback started", {
              id: messageMeta.id,
              deviceSentDate: messageMeta.deviceSentDate,
              speechToAudioLatencyMs: Date.now() - new Date(messageMeta.deviceSentDate).getTime(),
            });
          }
          window.dispatchEvent(new CustomEvent("emotii-playback-start", { detail: { userId, ...messageMeta } }));
        })
        .catch((err) => {
          console.error("Playback error:", err);
          finish(false);
        });
    });
  }

  canProcessImediatly = (messages, user) => {
    if (this.playing) {
      return false;
    }

    const unspokenMessages = messages.filter(
      (msg) => !this.spokenMessages.has(msg.id)
    );

    if (unspokenMessages.length === 0) {
      return false;
    }

    const earliestUnspoken = unspokenMessages.reduce((earliest, current) => {
      return new Date(current.deviceSentDate) <
        new Date(earliest.deviceSentDate)
        ? current
        : earliest;
    });

    if (!earliestUnspoken) return false;

    const translation = findTranslatedTextForUser(
      earliestUnspoken.call_message_translations,
      user
    );

    return translation != null;
  };

  /**
   * Recursive method to speak each message one at a time.
   */
  async speakMessagesRecursively(
    messages,
    index,
    speakAudio,
    user,
    outputDeviceId,
    setDimVolume
  ) {
    if (index >= messages.length) {
      return;
    }

    const message = messages[index];
    const translation = message.translation;

    if (!translation) {
      // Even if missing translation, mark as spoken and continue
      this.spokenMessages.add(message.id);
      return this.speakMessagesRecursively(
        messages,
        index + 1,
        speakAudio,
        user,
        outputDeviceId,
        setDimVolume
      );
    }

    const messageMeta = { id: message.id, deviceSentDate: message.device_sent_date };

    const audioUrl = await this.convertToAudio(
      speakAudio,
      user.call_language,
      translation,
      message.user_id,
      messageMeta
    );

    if (audioUrl) {
      const played = await this.playAudio(audioUrl, outputDeviceId, setDimVolume, message.user_id, messageMeta);
      if (!played) {
        return;
      }
    }

    this.spokenMessages.add(message.id);

    return this.speakMessagesRecursively(
      messages,
      index + 1,
      speakAudio,
      user,
      outputDeviceId,
      setDimVolume
    );
  }

  async speakMessages(
    messagesToSpeak,
    speakAudio,
    user,
    outputDeviceId,
    setDimVolume
  ) {
    if (!messagesToSpeak || messagesToSpeak.length === 0) {
      return;
    }

    this.playing = true;

    // Start recursive speaking
    await this.speakMessagesRecursively(
      messagesToSpeak,
      0,
      speakAudio,
      user,
      outputDeviceId,
      setDimVolume
    );

    this.playing = false;
    this.cleanProcessed();
  }

  async processQueue(speakAudio, user, outputDeviceId, messages, setDimVolume) {
    if (this.playing || this.processing) {
      this.pendingProcess = true;
      return;
    }

    this.processing = true;
    this.cancelScheduledProcessing();

    do {
      this.pendingProcess = false; // reset before running

      const messagesToSpeak = this.getSpeakableMessages(messages, user);
      if (messagesToSpeak.length === 0) {
        this.processing = false;
        return;
      }

      await this.speakMessages(
        messagesToSpeak,
        speakAudio,
        user,
        outputDeviceId,
        setDimVolume
      );
    } while (this.pendingProcess); // if new run was triggered during processing

    this.processing = false;
  }

  scheduleProcessQueue(
    speakAudio,
    user,
    outputDeviceId,
    messages,
    setDimVolume
  ) {
    this.cancelScheduledProcessing(); // invalidate any previous timer

    this.processingTimer = setTimeout(() => {
      this.processQueue(
        speakAudio,
        user,
        outputDeviceId,
        messages,
        setDimVolume
      );
    }, this.queueTimeout);
  }

  cancelScheduledProcessing() {
    if (this.processingTimer) {
      clearTimeout(this.processingTimer);
      this.processingTimer = null;
    }
  }

  /**
   * Call this from outside when new messages arrive
   */
  async processMessages(
    messages,
    { speakAudio, user, outputDeviceId, setDimVolume }
  ) {
    this.stopped = false;
    this.currentMessages = messages;

    const canProcess = this.canProcessImediatly(messages, user);

    if (!speakAudio) {
      this.stop();
    }

    if (canProcess) {
      this.processQueue(
        speakAudio,
        user,
        outputDeviceId,
        messages,
        setDimVolume
      );
    } else {
      this.scheduleProcessQueue(
        speakAudio,
        user,
        outputDeviceId,
        messages,
        setDimVolume
      );
    }
  }
}
